import React, { useEffect, useState, useCallback } from 'react';
import ApiClient from '../utils/ApiClient';

function formatAgo(iso) {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return String(iso);
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}

function formatUptime(ms) {
  if (!ms) return '';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function StatCard({ label, value, detail, color }) {
  return (
    <div className="bg-white shadow rounded-lg px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-gray-500 truncate">{label}</div>
      <div className={`text-2xl font-semibold ${color || 'text-gray-900'}`}>{value}</div>
      {detail && <div className="text-xs text-gray-500 truncate">{detail}</div>}
    </div>
  );
}

function pctColor(pct, amber, red) {
  if (pct >= red) return 'text-red-600';
  if (pct >= amber) return 'text-amber-600';
  return 'text-green-600';
}

function statusBadge(status) {
  const s = (status || '').toUpperCase();
  const cls =
    s === 'DONE'              ? 'bg-green-100 text-green-800' :
    s === 'PREVIEW_COMPLETE'  ? 'bg-green-100 text-green-800' :
    s === 'COMPLETED'         ? 'bg-green-100 text-green-800' :
    s === 'SUBMITTED'         ? 'bg-blue-100 text-blue-800'  :
    s === 'PROCESSING'        ? 'bg-blue-100 text-blue-800'  :
    s === 'REQUESTED'         ? 'bg-blue-100 text-blue-800'  :
    s === 'FATAL'             ? 'bg-red-100 text-red-800'    :
    s === 'CANCELLED'         ? 'bg-red-100 text-red-800'    :
    s === 'INITIATED'         ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-700';
  return <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${cls}`}>{status || '—'}</span>;
}

export default function StatusPage() {
  const [summary, setSummary] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [reports, setReports] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const fetchAll = useCallback(async () => {
    setError(null);
    try {
      const [s, f, r, j, sv] = await Promise.all([
        ApiClient.get('/api/admin/status/summary').catch(() => ({ data: null })),
        ApiClient.get('/api/admin/status/feeds?limit=25').catch(() => ({ data: [] })),
        ApiClient.get('/api/admin/status/reports?limit=15').catch(() => ({ data: [] })),
        ApiClient.get('/api/admin/status/jobs').catch(() => ({ data: [] })),
        ApiClient.get('/api/admin/status/server').catch(() => ({ data: null })),
      ]);
      setSummary(s.data); setFeeds(f.data || []); setReports(r.data || []);
      setJobs(j.data || []); setServer(sv.data);
      setRefreshedAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30000); // auto-refresh every 30s
    return () => clearInterval(id);
  }, [fetchAll]);

  if (loading) return <div className="p-4">Loading status…</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  const lastSync = summary?.last_sync;
  const lastClear = summary?.last_clear;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">System Status</h1>
          <p className="text-xs text-gray-500">
            Auto-refresh every 30s · last fetched {refreshedAt ? formatAgo(refreshedAt.toISOString()) : '—'}
          </p>
        </div>
        <button onClick={fetchAll}
                className="px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-500">
          Refresh now
        </button>
      </div>

      {/* Headline cards */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Today</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="MFN orders today" value={summary?.orders_today_mfn ?? 0} />
          <StatCard label="FBA orders today" value={summary?.orders_today_fba ?? 0} />
          <StatCard label="Open MFN orders"  value={summary?.orders_open_mfn ?? 0}
                    color={(summary?.orders_open_mfn ?? 0) > 10 ? 'text-amber-600' : 'text-gray-900'} />
          <StatCard label="Unassigned MFN"   value={summary?.orders_unassigned_mfn ?? 0}
                    color={(summary?.orders_unassigned_mfn ?? 0) > 0 ? 'text-red-600' : 'text-green-600'} />
          <StatCard label="Local inv. avail" value={summary?.local_inventory_available ?? 0} />
          <StatCard label="Feeds today"      value={summary?.feeds_submitted_today ?? 0} />
        </div>
      </section>

      {/* Server stats */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Server</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="JVM uptime"
                    value={formatUptime(server?.jvm_uptime_ms)}
                    detail={`Java ${server?.jvm_version || '?'}`} />
          <StatCard label="Heap used"
                    value={`${server?.heap_used_pct ?? 0}%`}
                    detail={`${server?.heap_used_mb}/${server?.heap_max_mb} MB`}
                    color={pctColor(server?.heap_used_pct ?? 0, 80, 92)} />
          <StatCard label="Threads (live)"
                    value={server?.threads_live ?? 0}
                    detail={`peak ${server?.threads_peak ?? 0}`} />
          <StatCard label="CPU load (1m)"
                    value={(server?.load_avg_1m >= 0 ? server.load_avg_1m.toFixed(2) : 'n/a')}
                    detail={`${server?.cpu_cores ?? 0} cores`}
                    color={pctColor(((server?.load_avg_1m || 0) / (server?.cpu_cores || 1)) * 100, 70, 100)} />
          <StatCard label="Last manual SYNC"
                    value={formatAgo(lastSync?.submitted_at)}
                    detail={lastSync?.feed_submission_id ? `feed ${lastSync.feed_submission_id}` : '—'} />
        </div>
      </section>

      {/* Recent feed submissions */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Recent Feed Submissions</h2>
        <div className="text-xs text-gray-500 mb-1">
          Last manual CLEAR: {formatAgo(lastClear?.submitted_at)}
          {lastClear?.feed_submission_id && ` (feed ${lastClear.feed_submission_id})`}
        </div>
        <div className="overflow-x-auto bg-white shadow rounded-lg ring-1 ring-black ring-opacity-5">
          <table className="min-w-full divide-y divide-gray-300 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Submitted</th>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Operation</th>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Feed ID</th>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Status</th>
                <th className="py-2 px-3 text-right font-semibold text-gray-900">Messages</th>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Job</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {feeds.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="py-1.5 px-3 text-gray-700 whitespace-nowrap">
                    {f.submitted_at} <span className="text-gray-400">· {formatAgo(f.submitted_at)}</span>
                  </td>
                  <td className="py-1.5 px-3 text-gray-700">{f.operation_type || '—'}</td>
                  <td className="py-1.5 px-3 text-gray-500 font-mono text-xs">{f.feed_submission_id || '—'}</td>
                  <td className="py-1.5 px-3">{statusBadge(f.status)}</td>
                  <td className="py-1.5 px-3 text-right text-gray-700">{f.message_count ?? '—'}</td>
                  <td className="py-1.5 px-3 text-gray-400 font-mono text-xs">{(f.job_id || '').slice(0, 8)}</td>
                </tr>
              ))}
              {feeds.length === 0 && (
                <tr><td colSpan="6" className="py-4 px-3 text-center text-gray-500">No feeds.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recent reports */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Recent Reports</h2>
        <div className="overflow-x-auto bg-white shadow rounded-lg ring-1 ring-black ring-opacity-5">
          <table className="min-w-full divide-y divide-gray-300 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Submitted</th>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Report Type</th>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Status</th>
                <th className="py-2 px-3 text-left font-semibold text-gray-900">Completed</th>
                <th className="py-2 px-3 text-right font-semibold text-gray-900">Processed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reports.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="py-1.5 px-3 text-gray-700 whitespace-nowrap">
                    {r.submitted_date} <span className="text-gray-400">· {formatAgo(r.submitted_date)}</span>
                  </td>
                  <td className="py-1.5 px-3 text-gray-700 font-mono text-xs">{r.report_type}</td>
                  <td className="py-1.5 px-3">{statusBadge(r.report_processing_status)}</td>
                  <td className="py-1.5 px-3 text-gray-500">{r.completed_date || '—'}</td>
                  <td className="py-1.5 px-3 text-right text-gray-700">{r.processed ?? '—'}</td>
                </tr>
              ))}
              {reports.length === 0 && (
                <tr><td colSpan="5" className="py-4 px-3 text-center text-gray-500">No report runs.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Discovered + Monitored jobs */}
      <JobsTable jobs={jobs} />
    </div>
  );
}

function jobStatusColor(status) {
  if (status === 'SUCCESS') return 'bg-green-100 text-green-800';
  if (status === 'FAILED')  return 'bg-red-100 text-red-800';
  if (status === 'MISSED')  return 'bg-amber-100 text-amber-800';
  if (status === 'RUNNING') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-600';
}

function JobsTable({ jobs }) {
  const [expandedName, setExpandedName] = useState(null);
  const [runs, setRuns] = useState({});
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [running, setRunning] = useState({});
  const [runMsg, setRunMsg] = useState({});

  const handleRun = async (e, name) => {
    e.stopPropagation();
    setRunning(s => ({ ...s, [name]: true }));
    setRunMsg(s => ({ ...s, [name]: null }));
    try {
      const resp = await ApiClient.post(`/api/admin/status/jobs/${encodeURIComponent(name)}/run`);
      setRunMsg(s => ({ ...s, [name]: resp.data?.message || 'kicked off' }));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'failed';
      setRunMsg(s => ({ ...s, [name]: `error: ${msg}` }));
    } finally {
      setRunning(s => ({ ...s, [name]: false }));
      setTimeout(() => setRunMsg(s => ({ ...s, [name]: null })), 8000);
    }
  };

  // Dedup: registry wins over discovered for the same name.
  const seen = new Set();
  const merged = [];
  for (const j of jobs) {
    if (j.source === 'registry') {
      seen.add(j.name);
      merged.push(j);
    }
  }
  for (const j of jobs) {
    if (j.source !== 'registry' && !seen.has(j.name)) merged.push(j);
  }

  const handleExpand = async (name) => {
    if (expandedName === name) { setExpandedName(null); return; }
    setExpandedName(name);
    if (runs[name]) return;
    setLoadingRuns(true);
    try {
      const resp = await ApiClient.get(`/api/admin/status/jobs/${encodeURIComponent(name)}/runs?limit=20`);
      setRuns(r => ({ ...r, [name]: resp.data || [] }));
    } catch (_e) { /* swallow */ }
    finally { setLoadingRuns(false); }
  };

  return (
    <section>
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Backend Scheduled Jobs</h2>
      <div className="text-xs text-gray-500 mb-1">
        Registry rows (authoritative @MonitoredJob runs) plus any @Scheduled methods not yet annotated.
        Click a registry row to see recent runs.
      </div>
      <div className="overflow-x-auto bg-white shadow rounded-lg ring-1 ring-black ring-opacity-5">
        <table className="min-w-full divide-y divide-gray-300 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-2 px-3 text-left font-semibold text-gray-900">Job</th>
              <th className="py-2 px-3 text-left font-semibold text-gray-900">Schedule</th>
              <th className="py-2 px-3 text-left font-semibold text-gray-900">Last status</th>
              <th className="py-2 px-3 text-left font-semibold text-gray-900">Last run</th>
              <th className="py-2 px-3 text-left font-semibold text-gray-900">Last success</th>
              <th className="py-2 px-3 text-right font-semibold text-gray-900">Failures</th>
              <th className="py-2 px-3 text-left font-semibold text-gray-900">Last alert</th>
              <th className="py-2 px-3 text-right font-semibold text-gray-900">Run</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {merged.map((j, idx) => (
              <React.Fragment key={`${j.name}.${idx}`}>
                <tr
                  className={`hover:bg-gray-50 ${j.source === 'registry' ? 'cursor-pointer' : ''}`}
                  onClick={() => j.source === 'registry' && handleExpand(j.name)}
                >
                  <td className="py-1.5 px-3 text-gray-700">
                    <div className="font-medium">{j.display_name || j.name}</div>
                    <div className="text-xs text-gray-400 font-mono">{j.name}</div>
                  </td>
                  <td className="py-1.5 px-3 text-gray-700 text-xs font-mono">{j.schedule_desc || '—'}</td>
                  <td className="py-1.5 px-3">
                    {j.last_status
                      ? <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${jobStatusColor(j.last_status)}`}>{j.last_status}</span>
                      : (j.last_activity_hint
                          ? <span className="text-xs text-gray-500">hint: {j.last_activity_hint}</span>
                          : <span className="text-xs text-gray-400">—</span>)}
                  </td>
                  <td className="py-1.5 px-3 text-gray-500 text-xs">{j.last_run_at ? `${j.last_run_at} (${formatAgo(j.last_run_at)})` : '—'}</td>
                  <td className="py-1.5 px-3 text-gray-500 text-xs">{j.last_success_at ? formatAgo(j.last_success_at) : '—'}</td>
                  <td className={`py-1.5 px-3 text-right text-xs font-medium ${(j.consecutive_failures || 0) > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                    {j.consecutive_failures || 0}
                  </td>
                  <td className="py-1.5 px-3 text-xs text-gray-500">
                    {j.last_alert_at
                      ? <><span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${jobStatusColor(j.last_alerted_status)}`}>{j.last_alerted_status}</span> <span className="ml-1 text-gray-400">{formatAgo(j.last_alert_at)}</span></>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-1.5 px-3 text-right">
                    {j.source === 'registry' ? (
                      <>
                        <button
                          type="button"
                          disabled={!!running[j.name]}
                          onClick={(e) => handleRun(e, j.name)}
                          className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-100 disabled:opacity-50"
                        >
                          {running[j.name] ? 'Running…' : 'Run'}
                        </button>
                        {runMsg[j.name] && (
                          <div className={`text-[10px] mt-0.5 ${runMsg[j.name].startsWith('error') ? 'text-red-600' : 'text-gray-500'}`}>
                            {runMsg[j.name]}
                          </div>
                        )}
                      </>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                </tr>
                {expandedName === j.name && (
                  <tr>
                    <td colSpan="8" className="bg-gray-50 px-3 py-2">
                      {loadingRuns && !runs[j.name] ? (
                        <div className="text-xs text-gray-500">Loading runs…</div>
                      ) : (runs[j.name] || []).length === 0 ? (
                        <div className="text-xs text-gray-500">No runs recorded yet.</div>
                      ) : (
                        <table className="min-w-full text-xs">
                          <thead className="text-gray-600">
                            <tr>
                              <th className="text-left py-1 pr-3">Started</th>
                              <th className="text-left py-1 pr-3">Finished</th>
                              <th className="text-left py-1 pr-3">Status</th>
                              <th className="text-right py-1 pr-3">Duration (ms)</th>
                              <th className="text-left py-1 pr-3">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(runs[j.name] || []).map(r => (
                              <tr key={r.id} className="border-t border-gray-200">
                                <td className="py-1 pr-3 font-mono">{r.started_at}</td>
                                <td className="py-1 pr-3 font-mono">{r.finished_at || '—'}</td>
                                <td className="py-1 pr-3"><span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${jobStatusColor(r.status)}`}>{r.status}</span></td>
                                <td className="py-1 pr-3 text-right">{r.duration_ms ?? '—'}</td>
                                <td className="py-1 pr-3 text-red-700 font-mono truncate max-w-md">{r.error_excerpt ? r.error_excerpt.split('\n')[0] : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {merged.length === 0 && (
              <tr><td colSpan="8" className="py-4 px-3 text-center text-gray-500">No jobs discovered.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

